import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { agGridThemes } from '../../../@shared/ag-grid-themes';
import { deriveDynamicColumnDefs } from '../../../@shared/ag-grid-columns';
import { GridCopyMenuComponent } from '../../../@shared/grid-copy-menu/grid-copy-menu.component';
import { CopySourceButtonComponent } from '../../../@shared/copy-source-button/copy-source-button.component';
import { SearchComboboxComponent } from '../../../@shared/search-combobox/search-combobox.component';
// #colCombobox — ViewChild ref lets onDbChange punt focus to the
// Collection combobox after the collection list loads (IT-F2-96 —
// Enter-select on the DB combobox jumps to the next selector).
import {
  MongoExplorerService,
  MongoStack,
  MongoDatabase,
} from '../../../@services/mongo-explorer.service';
import { ToastService } from '../../../@services/toast.service';

// MongoDB Explorer pane — one instance per stack (V1 or V2). Same UI,
// different backing Mongo (mongoService.client for V2, .legacy_client
// for V1). Data-agnostic AG-Grid: columns derived from whatever fields
// the rows carry. Editable cells (except _id / __v) — cell-edit-stopped
// event fires PUT /rest/mongo-explorer/rows/:id per change, with a
// per-cell confirm dialog naming the field + old/new value so operator
// can't fat-finger a critical config edit.
@Component({
  standalone: true,
  selector: 'app-mongo-explorer',
  imports: [AgGridAngular, CommonModule, FormsModule, SearchComboboxComponent, GridCopyMenuComponent, CopySourceButtonComponent],
  templateUrl: './mongo-explorer.component.html',
})
export class MongoExplorerComponent implements OnInit, OnChanges {
  @Input() stack: MongoStack = 'v2';

  databases: MongoDatabase[] = [];
  collections: string[] = [];
  // With the infinite row model the grid manages row data via its
  // datasource — we DON'T bind rows into [rowData]. Kept for the
  // status-bar / count derivation only.
  rows: any[] = [];
  columnDefs: any[] = [];

  selectedDb = '';
  selectedCol = '';

  loading = false;
  loadedAt: string | null = null;

  // Server-side pagination state (IT-F2-95). AG-Grid infinite row model
  // pulls blocks on scroll; cacheBlockSize=100 balances round-trip cost
  // vs per-scroll fetch chatter. maxBlocksInCache=10 caps memory so
  // 100k+ collections never load more than 1000 rows worth into JS.
  private readonly CACHE_BLOCK_SIZE = 100;
  private readonly MAX_BLOCKS_IN_CACHE = 10;
  totalCount: number | null = null;      // known after first block fetch
  private _sampleRowsForColumnDefs: any[] = [];
  // Datasource wire-race guard: on first collection pick the
  // <ag-grid-angular> is added to the DOM by *ngIf during the same
  // async flow that fetches the column-def sample. AG-Grid's
  // onGridReady may not have fired by the time the sample resolves —
  // so we stash the datasource here and _wireDatasourceIfReady()
  // gets called from BOTH the fetch-side AND onGridReady. Whichever
  // completes second actually installs it.
  private _pendingDatasource: any = null;

  // Combobox needs a string[] not a MongoDatabase[]; recompute on demand.
  get databaseNames(): string[] { return (this.databases || []).map((d) => d.name); }

  theme = agGridThemes.dark;
  grid: any;

  @ViewChild('colCombobox') colCombobox?: SearchComboboxComponent;

  constructor(private svc: MongoExplorerService, private toast: ToastService) {
    this.init_grid();
  }

  async ngOnInit() {
    await this.loadDatabases();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['stack'] && !changes['stack'].firstChange) {
      // Stack flipped — reset picker state + reload from scratch.
      this.selectedDb = '';
      this.selectedCol = '';
      this.collections = [];
      this.rows = [];
      this.columnDefs = [];
      this.totalCount = null;
      await this.loadDatabases();
    }
  }

  async loadDatabases() {
    this.loading = true;
    try {
      this.databases = await this.svc.listDatabases(this.stack);
    } finally {
      this.loading = false;
    }
  }

  async onDbChange(db: string) {
    this.selectedDb = db;
    this.selectedCol = '';
    this.collections = [];
    this.rows = [];
    this.columnDefs = [];
    if (!db) return;
    this.loading = true;
    try {
      this.collections = await this.svc.listCollections(this.stack, db);
    } finally {
      this.loading = false;
    }
    // IT-F2-96 — punt keyboard focus to the Collection combobox after
    // the collection list resolves so the operator can pick without a
    // mouse. Guard on non-empty list: don't focus into a disabled input.
    if (this.collections.length > 0) {
      this.colCombobox?.focus();
    }
  }

  async onColChange(col: string) {
    this.selectedCol = col;
    this.rows = [];
    this.columnDefs = [];
    this.totalCount = null;
    if (!col) return;
    await this.loadFirstPageAndWireDatasource();
  }

  /**
   * Pull a tiny sample synchronously so we have a real row shape to
   * derive column defs from, then wire the infinite-model datasource
   * (which handles the actual paginated fetches — including the first
   * block, EVERY block goes through the datasource so filter/sort
   * changes always land against a fresh backend query, not a cached
   * memory copy). (IT-F2-95)
   */
  async loadFirstPageAndWireDatasource() {
    if (!this.selectedDb || !this.selectedCol) return;
    this.loading = true;
    try {
      // Tiny sample (limit=5) to derive column defs from. This is a
      // separate call from the grid's first-block fetch — grid's own
      // getRows() populates the visible rows. Keeping this sample small
      // so the operator doesn't wait for a 100-row fetch just to see
      // column headers.
      const sample = await this.svc.listRowsPage(this.stack, this.selectedDb, this.selectedCol, {
        skip: 0,
        limit: 5,
        count: false,
      });
      if (!sample) return;
      // Editable everywhere except _id / __v / _explorer_updated_at
      // (backend-managed audit stamp).
      this.columnDefs = deriveDynamicColumnDefs(sample.rows, {
        isEditable: (f: string) => f !== '_id' && f !== '__v' && f !== '_explorer_updated_at',
      });
      this.loadedAt = new Date().toISOString();
      // Stash the datasource — onGridReady may not have fired yet on
      // first collection pick (see _pendingDatasource comment above).
      this._pendingDatasource = this._buildDatasource();
      this._wireDatasourceIfReady();
    } finally {
      this.loading = false;
    }
  }

  private _wireDatasourceIfReady() {
    const api = this.grid?.api;
    const ds = this._pendingDatasource;
    if (!api || !ds) return;
    // v33+ API — setDatasource is deprecated. Calling this purges the
    // grid's block cache automatically, so a fresh getRows(startRow=0)
    // will fire against the new datasource.
    api.setGridOption('datasource', ds);
    this._pendingDatasource = null;
    if (api.autoSizeAllColumns) {
      setTimeout(() => api.autoSizeAllColumns(false), 100);
    }
  }

  private _buildDatasource(): any {
    return {
      // rowCount unknown until first getRows fetch resolves with total.
      // Grid renders a "loading more" scrollbar in the meantime.
      rowCount: undefined,
      getRows: async (params: any) => {
        const startRow = params.startRow;
        const endRow = params.endRow;
        const blockSize = endRow - startRow;
        const filterModel = params.filterModel || {};
        const sortModel = params.sortModel || [];
        // Fetch count on every first-block pull (startRow==0). Grid
        // resets to block 0 on any filter/sort change so the status
        // bar total always reflects the current filtered subset.
        const includeCount = startRow === 0;
        const res = await this.svc.listRowsPage(this.stack, this.selectedDb, this.selectedCol, {
          skip: startRow,
          limit: blockSize,
          filterModel,
          sortModel,
          count: includeCount,
        });
        if (!res) { params.failCallback(); return; }
        // If we know the total, tell grid the last row so scrollbar is
        // exact. If we don't know it yet, pass -1 unless we just got
        // less than blockSize (last block).
        let lastRow = -1;
        if (typeof res.total === 'number') {
          lastRow = res.total;
          this.totalCount = res.total;
        } else if (res.rows.length < blockSize) {
          lastRow = startRow + res.rows.length;
        }
        params.successCallback(res.rows, lastRow);
      },
    };
  }

  /**
   * ag-grid fires cellEditingStopped for every commit AND every cancel.
   * We only PUT when the value actually changed. Confirm dialog names
   * the field + old/new value so operator can't fat-finger a critical
   * config edit. On failure we revert the cell to `oldValue`.
   */
  private async onCellEditingStopped(e: any) {
    if (!e || e.valueChanged !== true) return;
    if (e.oldValue === e.newValue) return;
    const rowId = e.data?._id;
    if (!rowId) {
      this.toast.error('Cannot save', 'Row has no _id — refusing to write without a stable identifier.');
      this._revertCell(e);
      return;
    }
    const field = e.colDef?.field;
    if (!field || field === '_id' || field === '__v') {
      this._revertCell(e);
      return;
    }
    const oldRepr = this._reprShort(e.oldValue);
    const newRepr = this._reprShort(e.newValue);
    if (!confirm(`Save this change?\n\nfield: ${field}\nold:   ${oldRepr}\nnew:   ${newRepr}\n\n${this.selectedDb}.${this.selectedCol}._id = ${rowId}`)) {
      this._revertCell(e);
      return;
    }
    const patch: Record<string, any> = {};
    patch[field] = e.newValue;
    const updated = await this.svc.updateRow(this.stack, this.selectedDb, this.selectedCol, String(rowId), patch);
    if (!updated) {
      this._revertCell(e);
      return;
    }
    // Merge server-authoritative fields back onto the row (e.g.
    // _explorer_updated_at) so the grid reflects the actual persisted
    // state, not just the operator's optimistic edit. Infinite row model
    // doesn't support applyTransaction — use rowNode.setData() on the
    // in-view node instead (falls through to a re-render).
    Object.assign(e.data, updated);
    if (e.node?.setData) e.node.setData(e.data);
    this.toast.success('Saved', `${field} updated`);
  }

  private _revertCell(e: any) {
    if (!e || !e.data || !e.colDef?.field) return;
    e.data[e.colDef.field] = e.oldValue;
    if (e.node?.setData) e.node.setData(e.data);
  }

  private _reprShort(v: any): string {
    if (v === null || v === undefined) return '(empty)';
    if (typeof v === 'string') return v.length > 60 ? v.slice(0, 60) + '…' : v;
    try { return JSON.stringify(v).slice(0, 80); } catch { return String(v); }
  }

  init_grid() {
    this.grid = {
      options: {
        context: this,
        // Server-side pagination (IT-F2-95). Infinite row model: grid
        // pulls blocks via datasource.getRows() as the operator scrolls.
        // cacheBlockSize=100 balances round-trip cost vs per-scroll
        // fetch chatter. maxBlocksInCache=10 caps memory so 100k+
        // collections never load more than ~1000 rows worth into JS.
        rowModelType: 'infinite',
        cacheBlockSize: this.CACHE_BLOCK_SIZE,
        maxBlocksInCache: this.MAX_BLOCKS_IN_CACHE,
        infiniteInitialRowCount: 1,
        // Multi-row selection disabled — infinite model doesn't reliably
        // support checkbox selection across not-yet-loaded blocks.
        suppressCellFocus: false,
        defaultColDef: {
          floatingFilter: true,
          filter: true,
          sortable: true,
          resizable: true,
          cellStyle: { 'text-align': 'left' },
        },
        sideBar: {
          toolPanels: [
            { id: 'columns', labelDefault: 'Columns', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
          ],
        },
        autoSizeStrategy: {
          type: 'fitCellContents',
          skipHeader: false,
        },
        singleClickEdit: false,
        stopEditingWhenCellsLoseFocus: true,
        onCellEditingStopped: (e: any) => this.onCellEditingStopped(e),
        onGridReady: (e: any) => {
          this.grid.api = e.api;
          // Catch the wire-race case: sample fetch may have already
          // resolved and stashed a pending datasource before the grid
          // finished mounting. Install it now.
          this._wireDatasourceIfReady();
        },
        onFirstDataRendered: (e: any) => e.api.autoSizeAllColumns(false),
      },
      // No statusBar — agTotalRowCountComponent + agFilteredRowCountComponent
      // are clientSide-row-model only (they call rowModel.forEachNodeAfter
      // Filter which the infinite model doesn't expose → AG-Grid errors
      // #225/#222 + a TypeError that KILLS grid init before onGridReady
      // fires). Total count is rendered in the toolbar via `totalCount`
      // instead (see mongo-explorer.component.html).
      statusBar: undefined,
    };
  }
}
